import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead, { getPropertySEO } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  MapPin, Bed, Maximize, Zap, Calendar as CalendarIcon, Clock, Heart, Share2,
  ChevronLeft, ChevronRight, Building, User, Phone, Mail, FileText,
  TrendingUp, Gavel, AlertCircle, Video, Eye
} from 'lucide-react';

// Virtual Tour Component for 360° embeds
const VirtualTourSection = ({ url, t }) => {
  const [showTour, setShowTour] = useState(false);
  
  if (!url) return null;
  
  // Extract embed URL based on provider
  const getEmbedUrl = (originalUrl) => {
    // YouTube
    if (originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be')) {
      const videoId = originalUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (videoId) return `https://www.youtube.com/embed/${videoId[1]}`;
    }
    // Matterport
    if (originalUrl.includes('matterport.com')) {
      return originalUrl.replace('/show/', '/show/?play=1&qs=1&');
    }
    // Kuula
    if (originalUrl.includes('kuula.co')) {
      return originalUrl;
    }
    // Default: return as-is for direct iframe URLs
    return originalUrl;
  };
  
  const embedUrl = getEmbedUrl(url);
  
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm" data-testid="virtual-tour-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg flex items-center">
          <Video className="h-5 w-5 mr-2 text-[#0071e3]" />
          {t('property.virtualTour', 'Tour Virtuale 360°')}
        </h3>
        <Button
          onClick={() => setShowTour(!showTour)}
          variant={showTour ? "default" : "outline"}
          size="sm"
          className={showTour ? "bg-[#0071e3] hover:bg-[#0D3D4A]" : ""}
          data-testid="toggle-virtual-tour-btn"
        >
          <Eye className="h-4 w-4 mr-2" />
          {showTour ? t('property.hideTour', 'Nascondi Tour') : t('property.viewTour', 'Visualizza Tour')}
        </Button>
      </div>
      
      {showTour && (
        <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-gray-200">
          <iframe
            src={embedUrl}
            title={t('property.virtualTour', 'Tour Virtuale 360°')}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking"
          />
        </div>
      )}
      
      {!showTour && (
        <div className="bg-gradient-to-br from-[#0071e3]/5 to-[#0071e3]/5 rounded-lg p-8 text-center border border-gray-100">
          <Video className="h-12 w-12 mx-auto text-[#0071e3] mb-3" />
          <p className="text-gray-600 mb-1">{t('property.tourDescription', 'Questo immobile include un tour virtuale interattivo')}</p>
          <p className="text-sm text-gray-500">{t('property.tourHint', 'Clicca "Visualizza Tour" per esplorare l\'immobile a 360°')}</p>
        </div>
      )}
    </div>
  );
};

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [visitDate, setVisitDate] = useState(null);
  const [visitTime, setVisitTime] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [bids, setBids] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchProperty();
    fetchBids();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [propertyId]);

  useEffect(() => {
    if (property?.auction_status === 'active') {
      connectWebSocket();
    }
  }, [property?.auction_status]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`/properties/${propertyId}`);
      setProperty(response.data);
      setIsFavorite(response.data.user_participation !== null);
    } catch (error) {
      toast.error('Errore nel caricamento dell\'immobile');
      navigate('/immobili');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await axios.get(`/bids/${propertyId}`);
      setBids(response.data);
    } catch (error) {
      console.error('Error fetching bids:', error);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '');
    const ws = new WebSocket(`${wsUrl}/ws/auction/${propertyId}`);
    
    ws.onopen = () => {
      setWsConnected(true);
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_bid') {
        setProperty(prev => ({
          ...prev,
          current_highest_bid: data.bid_amount,
          total_bids: data.total_bids
        }));
        fetchBids();
        toast.info(`Nuova offerta: €${formatPrice(data.bid_amount)}`);
      } else if (data.type === 'init') {
        setProperty(prev => ({
          ...prev,
          current_highest_bid: data.current_bid,
          total_bids: data.total_bids
        }));
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      console.log('WebSocket disconnected');
    };

    wsRef.current = ws;

    // Keep-alive ping
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleVisitRequest = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/immobili/${propertyId}` } });
      return;
    }

    if (!visitDate || !visitTime) {
      toast.error('Seleziona data e ora della visita');
      return;
    }

    try {
      await axios.post('/visits', {
        property_id: propertyId,
        visit_date: format(visitDate, 'yyyy-MM-dd'),
        visit_time: visitTime,
        notes: visitNotes
      });
      toast.success('Visita prenotata con successo!');
      setVisitDate(null);
      setVisitTime('');
      setVisitNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    }
  };

  const handleParticipationRequest = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/immobili/${propertyId}` } });
      return;
    }

    try {
      await axios.post('/participation', {
        property_id: propertyId,
        documents_uploaded: false
      });
      toast.success('Richiesta di partecipazione inviata!');
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    }
  };

  const handlePlaceBid = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/immobili/${propertyId}` } });
      return;
    }

    const amount = parseFloat(bidAmount);
    if (!amount || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    const minBid = (property.current_highest_bid || property.auction_start_price) + property.bid_increment;
    if (amount < minBid) {
      toast.error(`L'offerta minima è €${formatPrice(minBid)}`);
      return;
    }

    try {
      await axios.post('/bids', {
        property_id: propertyId,
        bid_amount: amount
      });
      toast.success('Offerta registrata con successo!');
      setBidAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'offerta');
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`/favorites/${propertyId}`);
        toast.success('Rimosso dai preferiti');
      } else {
        await axios.post(`/favorites/${propertyId}`);
        toast.success('Aggiunto ai preferiti');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error('Errore nell\'operazione');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header />
        <div className="container-custom py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-96 bg-gray-200 rounded-lg" />
            <div className="h-8 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const images = property.images?.length > 0 
    ? property.images 
    : ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200'];

  const currentBid = property.current_highest_bid || property.auction_start_price;
  const minNextBid = currentBid + (property.bid_increment || 1000);
  const canBid = property.user_participation?.participation_status === 'approved' && property.auction_status === 'active';

  // Generate dynamic SEO for this property
  const propertySEO = getPropertySEO(property);

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <SEOHead {...propertySEO} />
      <Header />

      <div className="container-custom py-8">
        {/* Breadcrumb */}
        <nav className="text-sm mb-6">
          <span className="text-gray-500 hover:text-[#0071e3] cursor-pointer" onClick={() => navigate('/immobili')}>
            Immobili
          </span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-700">{property.title}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
              <div className="aspect-[16/10] relative">
                <img
                  src={images[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}

                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  {property.auction_status === 'active' && (
                    <Badge className="bg-red-500 text-white auction-pulse">
                      <span className="inline-block w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />
                      GARA LIVE
                    </Badge>
                  )}
                  {property.auction_status === 'scheduled' && (
                    <Badge className="badge-auction">Gara Programmata</Badge>
                  )}
                </div>

                {/* Actions */}
                <div className="absolute top-4 right-4 flex space-x-2">
                  <button
                    onClick={toggleFavorite}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md transition ${
                      isFavorite ? 'bg-red-500 text-white' : 'bg-white/90 hover:bg-white'
                    }`}
                    data-testid="favorite-btn"
                  >
                    <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  <button className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition">
                    <Share2 className="h-5 w-5" />
                  </button>
                </div>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition ${
                        idx === currentImageIndex ? 'border-[#0071e3]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Property Info */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900  mb-4">
                {property.title}
              </h1>

              <div className="flex items-center text-gray-600 mb-6">
                <MapPin className="h-5 w-5 mr-2" />
                <span>{property.address}, {property.city}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Maximize className="h-6 w-6 mx-auto text-[#0071e3] mb-2" />
                  <div className="font-semibold">{property.sqm} m²</div>
                  <div className="text-sm text-gray-500">Superficie</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Bed className="h-6 w-6 mx-auto text-[#0071e3] mb-2" />
                  <div className="font-semibold">{property.rooms || 'N/D'}</div>
                  <div className="text-sm text-gray-500">Locali</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Building className="h-6 w-6 mx-auto text-[#0071e3] mb-2" />
                  <div className="font-semibold">{property.floor || 'N/D'}</div>
                  <div className="text-sm text-gray-500">Piano</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Zap className="h-6 w-6 mx-auto text-[#0071e3] mb-2" />
                  <div className="font-semibold">{property.energy_class || 'N/D'}</div>
                  <div className="text-sm text-gray-500">Classe Energ.</div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4">Descrizione</h3>
                <p className="text-gray-600 whitespace-pre-line">{property.description}</p>
              </div>

              {/* Property Details */}
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-lg mb-4">Caratteristiche</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tipologia</span>
                    <span className="font-medium capitalize">{property.property_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Condizione</span>
                    <span className="font-medium capitalize">{property.property_condition?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Zona OMI</span>
                    <span className="font-medium">{property.omi_zone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Quartiere</span>
                    <span className="font-medium">{property.area}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Virtual Tour Section */}
            <VirtualTourSection url={property.virtual_tour_url} t={t} />

            {/* Agent Info */}
            {property.agent && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-lg mb-4">Agente Immobiliare</h3>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {property.agent.first_name} {property.agent.last_name}
                    </div>
                    <div className="text-sm text-gray-600">{property.agent.company_name}</div>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Phone className="h-4 w-4 mr-1" />
                        {property.agent.phone}
                      </span>
                      <span className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {property.agent.email}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Auction Info */}
          <div className="space-y-6">
            {/* Auction Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden gold-accent">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg">Informazioni Gara di Vendita</h3>
                  {wsConnected && (
                    <span className="flex items-center text-xs text-green-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse" />
                      Live
                    </span>
                  )}
                </div>

                {/* Current Price */}
                <div className="mb-6">
                  <div className="text-sm text-gray-500 mb-1">
                    {property.total_bids > 0 ? 'Offerta Attuale' : 'Prezzo Base'}
                  </div>
                  <div className="text-3xl font-bold text-[#0071e3] tabular-nums">
                    €{formatPrice(currentBid)}
                  </div>
                  {property.total_bids > 0 && (
                    <div className="text-sm text-gray-500 mt-1">
                      {property.total_bids} offerte ricevute
                    </div>
                  )}
                </div>

                {/* Auction Details */}
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rilancio minimo</span>
                    <span className="font-medium">€{formatPrice(property.bid_increment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Caparra richiesta</span>
                    <span className="font-medium">€{formatPrice(property.deposit_amount)}</span>
                  </div>
                  {property.auction_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Data Gara</span>
                      <span className="font-medium">
                        {format(new Date(property.auction_date), 'dd/MM/yyyy', { locale: it })}
                      </span>
                    </div>
                  )}
                  {property.auction_time && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ora Gara</span>
                      <span className="font-medium">{property.auction_time}</span>
                    </div>
                  )}
                </div>

                {/* Bid Form (if auction is active and user is approved) */}
                {property.auction_status === 'active' && (
                  <div className="mt-6 pt-6 border-t">
                    {canBid ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-gray-500 mb-1 block">
                            Offerta minima: €{formatPrice(minNextBid)}
                          </label>
                          <div className="flex space-x-2">
                            <Input
                              type="number"
                              placeholder={`Min €${formatPrice(minNextBid)}`}
                              value={bidAmount}
                              onChange={(e) => setBidAmount(e.target.value)}
                              className="flex-1"
                              data-testid="bid-amount-input"
                            />
                            <Button 
                              onClick={handlePlaceBid} 
                              className="btn-accent"
                              data-testid="place-bid-btn"
                            >
                              <Gavel className="h-4 w-4 mr-2" />
                              Offri
                            </Button>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => setBidAmount(minNextBid.toString())}
                          >
                            +€{formatPrice(property.bid_increment)}
                          </Button>
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => setBidAmount((minNextBid + property.bid_increment).toString())}
                          >
                            +€{formatPrice(property.bid_increment * 2)}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <AlertCircle className="h-8 w-8 text-[#0071e3] mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-4">
                          Per partecipare alla gara devi essere abilitato
                        </p>
                        <Button 
                          className="w-full btn-primary"
                          onClick={handleParticipationRequest}
                          data-testid="request-participation-btn"
                        >
                          Richiedi Partecipazione
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Participation Request (if auction is scheduled) */}
                {property.auction_status === 'scheduled' && (
                  <div className="mt-6 pt-6 border-t">
                    {property.user_participation ? (
                      <div className="text-center">
                        <Badge className={
                          property.user_participation.participation_status === 'approved'
                            ? 'badge-active'
                            : 'badge-pending'
                        }>
                          {property.user_participation.participation_status === 'approved'
                            ? 'Partecipazione Approvata'
                            : 'In Attesa di Approvazione'}
                        </Badge>
                      </div>
                    ) : (
                      <Button 
                        className="w-full btn-primary"
                        onClick={handleParticipationRequest}
                        data-testid="request-participation-btn"
                      >
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Richiedi Partecipazione
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Visit Booking */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-lg mb-4">Prenota una Visita</h3>
              
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="book-visit-btn">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Prenota Visita
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Prenota una Visita</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Seleziona Data</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            {visitDate ? format(visitDate, 'PPP', { locale: it }) : 'Seleziona data'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={visitDate}
                            onSelect={setVisitDate}
                            disabled={(date) => date < new Date()}
                            locale={it}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Orario Preferito</label>
                      <Input
                        type="time"
                        value={visitTime}
                        onChange={(e) => setVisitTime(e.target.value)}
                        data-testid="visit-time-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Note (opzionale)</label>
                      <Input
                        placeholder="Eventuali richieste..."
                        value={visitNotes}
                        onChange={(e) => setVisitNotes(e.target.value)}
                      />
                    </div>
                    <Button 
                      className="w-full btn-primary" 
                      onClick={handleVisitRequest}
                      data-testid="confirm-visit-btn"
                    >
                      Conferma Prenotazione
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Recent Bids */}
            {bids.length > 0 && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-lg mb-4">Ultime Offerte</h3>
                <div className="space-y-3">
                  {bids.slice(0, 5).map((bid, index) => (
                    <div 
                      key={bid.bid_id} 
                      className={`flex justify-between items-center py-2 ${
                        index === 0 ? 'text-[#0071e3] font-semibold' : 'text-gray-600'
                      }`}
                    >
                      <span className="text-sm">
                        {format(new Date(bid.timestamp), 'HH:mm:ss', { locale: it })}
                      </span>
                      <span className="tabular-nums">€{formatPrice(bid.bid_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {property.documents?.length > 0 && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-lg mb-4">Documenti</h3>
                <div className="space-y-2">
                  {property.documents.map((doc, index) => (
                    <a
                      key={index}
                      href={doc}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-[#0071e3] hover:underline"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Documento {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
